import apiClient from "./client";

export const adminApi = {
  getDashboardStats: async () => {
    const response = await apiClient.get("/admin/dashboard/stats");
    return response.data;
  },

  getRecentActivity: async (limit = 10) => {
    const response = await apiClient.get(
      `/admin/dashboard/activity?limit=${limit}`,
    );
    return response.data;
  },

  getActionRequired: async () => {
    const response = await apiClient.get("/admin/dashboard/actions");
    return response.data;
  },

  getAuditLogs: async (params) => {
    const response = await apiClient.get("/admin/audit-logs", { params });
    return response.data;
  },

  getClients: async () => {
    const response = await apiClient.get("/admin/clients");
    return response.data;
  },

  getClientDetail: async (clientId) => {
    const response = await apiClient.get(`/admin/clients/${clientId}`);
    return response.data;
  },

  getClientStoreAssignment: async (clientId) => {
    const response = await apiClient.get(
      `/admin/clients/${clientId}/store-assignment`,
    );
    return response.data;
  },

  assignStoreToClient: async (clientId, storeRequestId, tenantId) => {
    const response = await apiClient.put(
      `/admin/clients/${clientId}/store-assignment`,
      { storeRequestId, tenantId },
    );
    return response.data;
  },

  createTenantForStoreRequest: async (
    clientId,
    storeRequestId,
    metadata,
  ) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/store-requests/${storeRequestId}/create-tenant`,
      metadata,
    );
    return response.data;
  },

  migrateClientUserToTenant: async (clientId, storeRequestId) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/store-requests/${storeRequestId}/migrate-user`,
    );
    return response.data;
  },

  getStoreDatabaseSettings: async () => {
    const response = await apiClient.get("/admin/store-database");
    return response.data;
  },

  getStoreOverview: async () => {
    const response = await apiClient.get("/admin/store-database/overview");
    return response.data;
  },

  getStorePlans: async () => {
    const response = await apiClient.get(
      "/admin/store-database/overview/plans",
    );
    return response.data;
  },

  updateStoreStatus: async (tenantId, isActive) => {
    const response = await apiClient.put(
      `/admin/store-database/overview/${tenantId}/status`,
      { isActive },
    );
    return response.data;
  },

  updateStorePlan: async (tenantId, payment) => {
    const response = await apiClient.post(
      `/admin/store-database/overview/${tenantId}/subscription`,
      payment,
    );
    return response.data;
  },

  verifyStoreDomainDns: async (tenantId, domain) => {
    const response = await apiClient.post(
      `/admin/store-database/overview/${tenantId}/domain/check`,
      { domain },
    );
    return response.data;
  },

  updateStoreDomain: async (tenantId, domain) => {
    const response = await apiClient.put(
      `/admin/store-database/overview/${tenantId}/domain`,
      { domain },
      { timeout: 150000 },
    );
    return response.data;
  },

  updateStoreMaintenanceMode: async (tenantId, enabled) => {
    const response = await apiClient.put(
      `/admin/store-database/overview/${tenantId}/maintenance`,
      { enabled },
      { timeout: 150000 },
    );
    return response.data;
  },

  updateStoreUserAccess: async (tenantId, userId, isActive) => {
    const response = await apiClient.put(
      `/admin/store-database/overview/${tenantId}/users/${userId}/status`,
      { isActive },
    );
    return response.data;
  },

  checkAllStoreHealth: async () => {
    const response = await apiClient.post(
      "/admin/store-database/overview/check-all",
    );
    return response.data;
  },

  checkStoreHealth: async (tenantId) => {
    const response = await apiClient.post(
      `/admin/store-database/overview/${tenantId}/check`,
    );
    return response.data;
  },

  renewStoreCertificate: async (tenantId) => {
    const response = await apiClient.post(
      `/admin/store-database/overview/${tenantId}/renew-certificate`,
      {},
      { timeout: 150000 },
    );
    return response.data;
  },

  updateStoreSetupStatus: async (tenantId, setupStatus) => {
    const response = await apiClient.put(
      `/admin/store-database/overview/${tenantId}/setup-status`,
      { setupStatus },
    );
    return response.data;
  },

  testStoreDatabaseConnection: async (settings) => {
    const response = await apiClient.post(
      "/admin/store-database/test",
      settings,
    );
    return response.data;
  },

  saveStoreDatabaseSettings: async (settings) => {
    const response = await apiClient.put("/admin/store-database", settings);
    return response.data;
  },

  getNginxServerSettings: async () => {
    const response = await apiClient.get("/admin/nginx-server");
    return response.data;
  },

  testNginxServerConnection: async (settings) => {
    const response = await apiClient.post("/admin/nginx-server/test", settings);
    return response.data;
  },

  saveNginxServerSettings: async (settings) => {
    const response = await apiClient.put("/admin/nginx-server", settings);
    return response.data;
  },

  streamStoreRequestNginxDeployment: async (
    clientId,
    storeRequestId,
    onProgress,
  ) => {
    const token = localStorage.getItem("token");
    const response = await fetch(
      `/api/v1/admin/clients/${clientId}/store-requests/${storeRequestId}/nginx-site`,
      {
        method: "POST",
        headers: {
          Accept: "application/x-ndjson",
          Authorization: token ? `Bearer ${token}` : "",
        },
      },
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || "Could not start Nginx deployment.");
    }
    if (!response.body) {
      throw new Error("The browser could not read deployment progress.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let result = null;
    const processLine = (line) => {
      if (!line.trim()) return;
      const event = JSON.parse(line);
      if (event.type === "progress") onProgress(event);
      if (event.type === "complete") result = event.data;
      if (event.type === "error") throw new Error(event.error);
    };

    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || "";
      lines.forEach(processLine);
      if (done) break;
    }
    processLine(buffer);
    if (!result) {
      throw new Error("The server ended deployment without a completion result.");
    }
    return result;
  },

  verifyClientEmail: async (clientId) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/verify-email`,
    );
    return response.data;
  },

  resendClientWelcomeEmail: async (clientId) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/resend-welcome`,
    );
    return response.data;
  },

  updateClientSecurityStatus: async (clientId, mustChangePassword) => {
    const response = await apiClient.put(
      `/admin/clients/${clientId}/security-status`,
      { mustChangePassword },
    );
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await apiClient.delete(`/admin/clients/${clientId}`);
    return response.data;
  },

  getClientDeletionRequests: async () => {
    const response = await apiClient.get("/admin/privacy/deletion-requests");
    return response.data;
  },

  reviewClientDeletionRequest: async (requestId, status, note = "") => {
    const response = await apiClient.put(
      `/admin/privacy/deletion-requests/${requestId}`,
      { status, note },
    );
    return response.data;
  },

  sendClientTemporaryPassword: async (clientId) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/temporary-password`,
    );
    return response.data;
  },

  startClientSupportSession: async (clientId, reason) => {
    const response = await apiClient.post(
      `/admin/clients/${clientId}/support-session`,
      { reason },
    );
    return response.data;
  },

  toggleUserStatus: async (userId, isActive) => {
    const response = await apiClient.put(`/admin/users/${userId}/status`, {
      isActive,
    });
    return response.data;
  },

  getEmailSettings: async () => {
    const response = await apiClient.get("/admin/email/settings");
    return response.data;
  },

  saveEmailSettings: async (settings) => {
    const response = await apiClient.put("/admin/email/settings", settings);
    return response.data;
  },

  sendTestEmail: async (recipient) => {
    const response = await apiClient.post("/admin/email/test", { recipient });
    return response.data;
  },

  sendStoreCompletionReminders: async (reminder) => {
    const response = await apiClient.post(
      "/admin/email/store-reminders",
      reminder,
    );
    return response.data;
  },

  getReminderRecipients: async (type) => {
    const response = await apiClient.get("/admin/email/reminders/recipients", {
      params: { type },
    });
    return response.data;
  },

  sendReminderEmails: async (type, reminder) => {
    const response = await apiClient.post(
      `/admin/email/reminders/${type}`,
      reminder,
    );
    return response.data;
  },

  sendPaymentReminder: async (storeRequestId) => {
    const response = await apiClient.post(
      `/admin/email/payment-reminders/${storeRequestId}`,
    );
    return response.data;
  },

  getStoreCompletionReminderLogs: async (params) => {
    const response = await apiClient.get("/admin/email/store-reminders/logs", {
      params,
    });
    return response.data;
  },
};
